/**
 * Design Tokens — Single Source of Truth
 *
 * This file defines the centralized design system for the entire application.
 * It feeds into tailwind.config.js (via design-tokens.cjs) and is available
 * as Tailwind utility classes.
 *
 * To change a color globally, change it HERE — it propagates everywhere.
 *
 * Naming convention uses fg/surface/line prefixes for clean Tailwind classes:
 *   text-fg, text-fg-secondary, bg-surface, bg-surface-hover, border-line, etc.
 *
 * For React components that need JS access, import from tokens.ts instead.
 */

export const colors = {
  // ─── Foreground (text & icons) ──────────────────────────────────
  // Usage: text-fg, text-fg-secondary, text-fg-muted
  'fg': '#191D23', // Primary text — headings, body copy
  'fg-secondary': '#6E6E6E', // Supporting text — labels, descriptions
  'fg-tertiary': '#475467', // Subtle text — table headers, metadata
  'fg-muted': '#C4C4C4', // Placeholder, disabled text
  'fg-dark': '#101828', // Extra emphasis — dark headings
  'fg-subtle': '#414942', // UI controls — drag handles, arrows
  'fg-inverse': '#FFFFFF', // Text on dark backgrounds

  // ─── Brand ──────────────────────────────────────────────────────
  // Usage: text-brand, bg-brand, border-brand
  'brand': '#06512F', // Primary brand green
  'brand-hover': '#2C6B4E', // Hover state
  'brand-active': '#087443', // Active/pressed state
  'brand-pressed': '#054628', // Button pressed state
  'brand-dark': '#245A42', // Hover on dark brand buttons
  'brand-deep': '#002110', // Darkest green — visited links, deep accents
  'brand-light': '#01e677', // Bright accent (badges, highlights)

  // ─── Surfaces (backgrounds) ─────────────────────────────────────
  // Usage: bg-surface, bg-surface-hover, bg-surface-muted
  'surface': '#FFFFFF', // Page and card backgrounds
  'surface-alt': '#F5F5F5', // Alternate surface
  'surface-alt-active': '#E7E7E7', // Pressed state on alt surfaces
  'surface-raised': '#FAFAFA', // Subtle raised surface
  'surface-muted': '#F4F7F5', // Card backgrounds (sage-50)
  'surface-hover': '#EEF5F2', // Hover state (pistachio)
  'surface-hover-deep': '#E5F2EA', // Deeper hover — hover-on-hover elements
  'surface-active': '#D6E8E0', // Pressed/selected state
  'surface-disabled': '#E2E2E2', // Disabled elements
  'surface-sunken': '#F9FAFB', // Recessed areas

  // ─── Lines (borders & dividers) ─────────────────────────────────
  // Usage: border-line, border-line-form, ring-line-focus
  'line': '#E2E2E2', // Standard borders
  'line-form': '#DEE2E6', // Form input borders
  'line-subtle': '#EAECF0', // Table dividers, light borders
  'line-focus': '#8EA69B', // Input focus ring (sage)
  'line-brand': '#06512F', // Brand accent borders

  // ─── Navigation (dark background context) ───────────────────────
  // Usage: bg-nav-bg, text-nav-text
  'nav-bg': '#06512F',
  'nav-bg-hover': '#2C6B4E',
  'nav-text': '#FFFFFF',
  'nav-text-muted': '#C1D1C6',
  'nav-tooltip-bg': '#D6E2DA',
  'nav-tooltip-text': '#191D23',

  // ─── Status ─────────────────────────────────────────────────────
  // Usage: text-error, bg-error-bg, border-error-border
  'error': '#D43D51',
  'error-bg': '#FDECEC',
  'error-border': '#E87676',
  'success': '#059669',
  'success-bright': '#28B643', // Bright success green — offers, highlights
  'success-bg': '#D1FAE5',
  'warning': '#D97706',
  'warning-bg': '#FEF3C7',
  'info': '#2563EB',
  'info-bg': '#DBEAFE',

  // ─── Overlay ────────────────────────────────────────────────────
  'modal-overlay': 'rgba(4, 30, 18, 0.5)',
}

export const fontSize = {
  // Gap size that Tailwind doesn't provide by default
  'xxs': ['10px', { lineHeight: '14px' }],
  // Tailwind defaults remain: xs=12px, sm=14px, base=16px, lg=18px, etc.
  // Override the full scale in Phase 2 after visual audit with Laura.
}
